import React from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '../../../../components';
import {
  Bar,
  Left,
  Caption,
  SizeWrap,
  SelectBox,
  Select,
  SelectChevron,
  Pager,
  PageBtn,
  NavBtn,
  PageLabel,
  Flip,
  Ellipsis,
} from './PlayersPagination.styles';

export type PlayersPaginationProps = {
  /** 1-based current page. */
  page: number;
  /** Total number of pages (≥ 1). */
  pageCount: number;
  /**
   * The rows-per-page selector, shown only when all three of `rowsPerPage`,
   * `rowsPerPageOptions` and `onRowsPerPageChange` are given. A list with a
   * fixed page size (the blog's article list) leaves them out.
   */
  rowsPerPage?: number;
  rowsPerPageOptions?: number[];
  /** 1-based index of the first row shown, and the last (for the "x–y of n" caption). */
  rangeStart: number;
  rangeEnd: number;
  total: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange?: (rows: number) => void;
};

type PageToken = number | 'gap';

// The windowed page list: 1 … (current-1, current, current+1) … last. Shows every
// page when there are few enough, ellipses otherwise.
const buildPages = (current: number, count: number): PageToken[] => {
  if (count <= 7) return Array.from({ length: count }, (_, i) => i + 1);
  const tokens: PageToken[] = [1];
  const left = Math.max(2, current - 1);
  const right = Math.min(count - 1, current + 1);
  if (left > 2) tokens.push('gap');
  for (let p = left; p <= right; p += 1) tokens.push(p);
  if (right < count - 1) tokens.push('gap');
  tokens.push(count);
  return tokens;
};

/**
 * Roster pagination — "Rows per page" selector + "x–y of n" caption on the left,
 * the prev / page-number / next pager on the right. Every interactive control is
 * at least 48×48; the active page is the brand-orange filled chip (the area's CTA
 * pairing). Stateless: the page owns `page` / `rowsPerPage` and re-slices. The
 * blog's article list uses it too, without the selector.
 */
export const PlayersPagination = ({
  page,
  pageCount,
  rowsPerPage,
  rowsPerPageOptions,
  rangeStart,
  rangeEnd,
  total,
  onPageChange,
  onRowsPerPageChange,
}: PlayersPaginationProps): React.ReactElement => {
  const { t } = useTranslation();

  return (
    <Bar>
      <Left>
        {rowsPerPage !== undefined && rowsPerPageOptions && onRowsPerPageChange ? (
          <SizeWrap>
            <Caption text="Rows per page" tag="span" />
            <SelectBox>
              <Select
                value={rowsPerPage}
                aria-label={t('Rows per page')}
                onChange={(event) => onRowsPerPageChange(Number(event.target.value))}
              >
                {rowsPerPageOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </Select>
              <SelectChevron>
                <Icon name="chevron-expand" size={14} />
              </SelectChevron>
            </SelectBox>
          </SizeWrap>
        ) : null}
        <Caption
          text="{{start}}–{{end}} of {{total}}"
          placeholders={{ start: rangeStart, end: rangeEnd, total }}
          tag="span"
        />
      </Left>

      <Pager>
        <NavBtn
          type="button"
          aria-label={t('Previous page')}
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          <Icon name="arrow-left" size={18} />
        </NavBtn>

        {buildPages(page, pageCount).map((token, index) =>
          token === 'gap' ? (
            <Ellipsis key={`gap-${index}`} text="…" tag="span" aria-hidden="true" />
          ) : (
            <PageBtn
              key={token}
              type="button"
              $active={token === page}
              aria-label={t('Page {{n}}', { n: token })}
              aria-current={token === page ? 'page' : undefined}
              onClick={() => onPageChange(token)}
            >
              <PageLabel text={String(token)} tag="span" />
            </PageBtn>
          ),
        )}

        <NavBtn
          type="button"
          aria-label={t('Next page')}
          disabled={page >= pageCount}
          onClick={() => onPageChange(page + 1)}
        >
          <Flip>
            <Icon name="arrow-left" size={18} />
          </Flip>
        </NavBtn>
      </Pager>
    </Bar>
  );
};

export default PlayersPagination;
